import { BrokerConnector, Broker } from './types'
import { ZerodhaConnector } from './ZerodhaConnector'
import { BinanceConnector } from './BinanceConnector'
import { NintradeConnector } from './NintradeConnector'
import { GrowwConnector } from './GrowwConnector'
import { NINXConnector } from './NINXConnector'
import {
  getSecureItem,
  setSecureItem
} from './utils/secureStorage'

export class ConnectorManager {
  // Register a connector with a broker ID and connector instance
  registerConnector(brokerId: string, connector: BrokerConnector) {
    this.connectors.set(brokerId, connector)
    this.updateBrokerStatus(brokerId, connector.isConnectedToBroker ? connector.isConnectedToBroker() : true)
  }
  private static instance: ConnectorManager
  private connectors: Map<string, BrokerConnector> = new Map()
  private brokers: Broker[] = [
    {
      id: 'ninx',
      name: 'NINX',
      logo: '/brokers/nin_logo_light.svg',
      isConnected: false,
      enabled: true
    },
    {
      id: 'nintrade',
      name: 'Nintrade',
      logo: '/brokers/nin_logo_dark.svg',
      isConnected: false,
      enabled: false
    },
    {
      id: 'zerodha',
      name: 'Zerodha',
      logo: '/brokers/zerodha_logo.svg',
      isConnected: false,
      enabled: false
    },
    {
      id: 'groww',
      name: 'Groww',
      logo: '/brokers/groww_logo.svg',
      isConnected: false,
      enabled: false
    },
    {
      id: 'binance',
      name: 'Binance',
      logo: '/brokers/binance_logo.svg',
      isConnected: false,
      enabled: false
    }
  ]

  private constructor() {
    // Check if we have stored Binance credentials in localStorage
    this.initializeFromLocalStorage()
  }

  private initializeFromLocalStorage() {
    // This will only work on the client side
    if (typeof window !== 'undefined') {
      // Load enabled broker from session storage
      const enabledBroker = getSecureItem('broker_enabled') || 'ninx'
      this.brokers.forEach(broker => {
        broker.enabled = broker.id === enabledBroker
      })

      // Check for stored credentials for all brokers
      this.brokers.forEach(broker => {
        // First check for broker_api_key format (used by ConnectorsPanel)
        let apiKey = getSecureItem(`${broker.id}_api_key`)
        let apiSecret = getSecureItem(`${broker.id}_api_secret`)

        // For backward compatibility, also check direct key format for Binance
        // if (broker.id === 'binance' && !apiKey) {
        //   apiKey = localStorage.getItem('binance_api_key')
        //   apiSecret = localStorage.getItem('binance_api_secret')
        // }

        // Special handling for Groww which only needs API key
        if (broker.id === 'groww' && apiKey) {
          const connector = new GrowwConnector(apiKey);
          this.connectors.set(broker.id, connector);
          this.updateBrokerStatus(broker.id, true);
          
          // Connect to ensure everything is properly initialized
          connector.connect().then(isConnected => {
            if (!isConnected) {
              console.warn(`Failed to reconnect to ${broker.name} with stored credentials`);
              this.updateBrokerStatus(broker.id, false);
            }
          });
        }
        // For other brokers that need both API key and secret
        else if (apiKey && apiSecret) {
          let connector: BrokerConnector;

          // Create the appropriate connector based on broker ID
          switch (broker.id) {
            case 'zerodha':
              connector = new ZerodhaConnector(apiKey, apiSecret);
              break;
            case 'nintrade':
              connector = new NintradeConnector(apiKey, apiSecret);
              break;
            case 'binance':
              connector = new BinanceConnector(apiKey, apiSecret);
              break;
            case 'ninx':
              connector = new NINXConnector(apiKey, apiSecret);
              break;
            default:
              console.warn(`No connector implementation for ${broker.id}`);
              return; // Skip this broker
          }

          // Store the connector in our map
          this.connectors.set(broker.id, connector)

          // Update the broker status to reflect it's connected
          this.updateBrokerStatus(broker.id, true)

          // Also connect to ensure everything is properly initialized
          connector.connect().then(isConnected => {
            if (!isConnected) {
              console.warn(
                `Failed to reconnect to ${broker.name} with stored credentials`
              )
              // If connection fails, update the status
              this.updateBrokerStatus(broker.id, false)
            }
          })
        }
      })
    }
  }

  static getInstance(): ConnectorManager {
    if (!ConnectorManager.instance) {
      ConnectorManager.instance = new ConnectorManager()
    }
    return ConnectorManager.instance
  }

  getBrokers(): Broker[] {
    return this.brokers
  }

  async connectBroker(
    brokerId: string,
    credentials: { apiKey: string; apiSecret: string }
  ): Promise<boolean> {
    try {
      let connector: BrokerConnector

      switch (brokerId) {
        case 'zerodha':
          connector = new ZerodhaConnector(
            credentials.apiKey,
            credentials.apiSecret
          )
          break
        case 'nintrade':
          connector = new NintradeConnector(
            credentials.apiKey,
            credentials.apiSecret
          )
          break
        case 'binance':
          connector = new BinanceConnector(
            credentials.apiKey,
            credentials.apiSecret
          )
          break
        case 'ninx':
          connector = new NINXConnector(
            credentials.apiKey,
            credentials.apiSecret
          )
          break
        case 'groww':
          // Groww only needs API key, not API secret
          connector = new GrowwConnector(credentials.apiKey)
          break
        // Add more cases for other brokers
        default:
          throw new Error('Unsupported broker')
      }

      const isConnected = await connector.connect()
      if (isConnected) {
        this.connectors.set(brokerId, connector)
        this.updateBrokerStatus(brokerId, true)
      }
      return isConnected
    } catch (error) {
      console.error(`Failed to connect to ${brokerId}:`, error)
      return false
    }
  }

  private updateBrokerStatus(brokerId: string, isConnected: boolean) {
    this.brokers = this.brokers.map(broker =>
      broker.id === brokerId ? { ...broker, isConnected } : broker
    )
  }

  updateBrokerEnabled(brokerId: string, enabled: boolean) {
    this.brokers = this.brokers.map(broker =>
      broker.id === brokerId
        ? { ...broker, enabled }
        : { ...broker, enabled: false }
    )
    if (typeof window !== 'undefined') {
      setSecureItem('broker_enabled', enabled ? brokerId : 'ninx')
    }
  }

  getConnector(brokerId: string): BrokerConnector | undefined {
    return this.connectors.get(brokerId)
  }

  /**
   * Register or update NINX connector after authentication
   * This method should be called after successful NINX authentication or session refresh
   */
  registerNinxConnector(): void {
    if (typeof window !== 'undefined') {
      // Import here to avoid circular dependencies
      const { getSecureItem } = require('./utils/secureStorage')
      const { hasValidNinxSession } = require('../../lib/auth-cookies')

      // Check for stored NINX credentials
      const apiKey = getSecureItem('ninx_api_key')
      const apiSecret = getSecureItem('ninx_api_secret')

      // Only register if we have valid credentials and session
      if (apiKey && apiSecret && hasValidNinxSession()) {
        try {
          // Check if connector is already registered and valid
          const existingConnector = this.connectors.get('ninx')
          if (existingConnector && existingConnector.isConnectedToBroker && existingConnector.isConnectedToBroker()) {
            console.log('NINX connector already registered and connected')
            return
          }

          const { NINXConnector } = require('./NINXConnector')
          const connector = new NINXConnector(apiKey, apiSecret)

          // Register the connector
          this.connectors.set('ninx', connector)
          this.updateBrokerStatus('ninx', true)

          console.log('NINX connector registered after authentication/session refresh')
        } catch (error) {
          console.error('Failed to register NINX connector:', error)
        }
      } else {
        console.log('Cannot register NINX connector: missing credentials or invalid session')
      }
    }
  }
}
